import { AddressInput } from "@/components/ui/address";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormError } from "@/components/ui/form-error";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NavigationStatus } from "@/components/utils/navigation-status";
import { ApprenticeshipProductType } from "@/types/apprenticeship/product";
import { ActionWrapper } from "@/utils/wretch";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircleIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useFetcher, useLoaderData } from "react-router-dom";
import { z } from "zod";

export default function SalesFormPage() {
    const fetcher = useFetcher<ActionWrapper<{}>>();
    const { products } = useLoaderData() as { products: ApprenticeshipProductType[] };

    const formSchema = z
        .object({
            name: z.string().min(1, "Le nom de l'entreprise est requis"),
            address: z.object({
                street: z.string({ required_error: "L'adresse est requise" }),
                city: z.string({ required_error: "La ville est requise" }),
                postal_code: z.string({ required_error: "Le code postal est requis" }),
                country: z.string().optional(),
                latitude: z.number().optional(),
                longitude: z.number().optional()
            }),
            phone: z.string().min(1, "Le numéro de téléphone est requis"),
            email: z.string().optional(),
            contrat_pro: z.boolean(),
            apprentissage: z.boolean(),
            product: z.string().min(1, "La formation est requis"),
            available_seats: z.number({ required_error: "Vous devez indiquer le nombre de place disponibles" }),
            comment: z.string().optional(),
            contact_first_name: z.string().optional(),
            contact_last_name: z.string().optional(),
            contact_role: z.string().optional(),
            contact_email: z.string().optional(),
            contact_phone: z.string().optional()
        })
        .refine(values => values.contrat_pro || values.apprentissage, { message: "Vous devez sélectionner au moins un type de contrat", path: ["apprentissage"] });

    const form = useForm<z.infer<typeof formSchema>>({
        mode: "onSubmit",
        resolver: zodResolver(formSchema),
        defaultValues: {
            contrat_pro: true,
            apprentissage: true,
            available_seats: 1,
            email: undefined,
            address: {
                street: undefined,
                postal_code: undefined,
                country: undefined,
                city: undefined,
                latitude: undefined,
                longitude: undefined
            }
        }
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        fetcher.submit(
            {
                ...values,
                street: values.address.street ?? null,
                postal_code: values.address.postal_code ?? null,
                city: values.address.city ?? null,
                latitude: values.address.latitude ?? null,
                longitude: values.address.longitude ?? null,
                product: values.product
                    ? {
                          id: values.product
                      }
                    : null
            },
            { method: "post", action: `/standalone/formulaires/alternance/offre-entreprise`, encType: "application/json" }
        );
    };

    useEffect(() => {}, [fetcher.data]);

    return (
        <>
            <div className="bg-muted/40">
                <div className="min-h-screen w-screen">
                    <div className="mx-auto max-w-screen-lg p-4">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 outline-none">
                                <Card>
                                    {fetcher.data?.success ? (
                                        <>
                                            <CardContent className="pt-6">
                                                <div className="flex items-center justify-center">
                                                    <div className="flex max-w-md items-center space-x-5 space-y-1 text-center">
                                                        <div>
                                                            <CheckCircleIcon className="h-10 w-10 text-green-500" />
                                                        </div>
                                                        <div>
                                                            <p>L'offre d'alternance a été enregistrée avec succès, vous pouvez maintenant fermer cette page</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </>
                                    ) : (
                                        <>
                                            <CardHeader>
                                                <CardTitle>Enregistrer une offre d'alternance entreprise</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-6">
                                                    <FormError errorMessage={fetcher.data?.message} className="mb-4" />
                                                    <div className="space-y-4">
                                                        <h2 className="font-medium">L'entreprise</h2>
                                                        <div className="grid gap-3 lg:grid-cols-2">
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="name"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel required>Nom de l'entreprise</FormLabel>
                                                                            <FormControl>
                                                                                <Input {...field} value={field.value} placeholder="Indiquer le nom de l'entreprise" onChange={field.onChange} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="phone"
                                                                    render={({ field }) => (
                                                                        <FormItem className="col-span-2">
                                                                            <FormLabel required>Numéro de téléphone</FormLabel>
                                                                            <FormControl>
                                                                                <Input placeholder="Numéro de téléphone" {...field} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="email"
                                                                    render={({ field }) => (
                                                                        <FormItem className="col-span-2">
                                                                            <FormLabel>Adresse email</FormLabel>
                                                                            <FormControl>
                                                                                <Input placeholder="Adresse email" {...field} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="address"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel required>Adresse postale de l'entreprise</FormLabel>
                                                                            <FormControl>
                                                                                <AddressInput
                                                                                    address={field.value}
                                                                                    onAddressChange={v => {
                                                                                        field.onChange(v);
                                                                                    }}
                                                                                />
                                                                            </FormControl>
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-4">
                                                        <h2 className="font-medium">L'offre d'alternance</h2>
                                                        <div className="grid gap-3 lg:grid-cols-2">
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="product"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel required>Parcours de formation associé</FormLabel>
                                                                            <FormControl>
                                                                                <div>
                                                                                    <Combobox
                                                                                        name={field.name}
                                                                                        placeholder="Sélectionner un parcours de formation"
                                                                                        options={products.map((product: ApprenticeshipProductType) => {
                                                                                            return {
                                                                                                value: product.id.toString(),
                                                                                                label: product.short_name || product.name || product.product_code || product.id.toString()
                                                                                            };
                                                                                        })}
                                                                                        defaultValue={field.value}
                                                                                        onValueChange={field.onChange}
                                                                                        className="w-full"
                                                                                    />
                                                                                </div>
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="available_seats"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel>Nombre d'alternants souhaités</FormLabel>
                                                                            <FormControl>
                                                                                <Input
                                                                                    {...field}
                                                                                    type="number"
                                                                                    value={field.value || ""}
                                                                                    placeholder="Nombre d'alternants souhaités"
                                                                                    onChange={e => {
                                                                                        try {
                                                                                            if (e.target.value.length > 0) {
                                                                                                const v = parseFloat(e.target.value);
                                                                                                if (!Number.isNaN(v)) {
                                                                                                    field.onChange(v);
                                                                                                }
                                                                                            } else {
                                                                                                field.onChange(0);
                                                                                            }
                                                                                        } catch (e) {}
                                                                                    }}
                                                                                />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div className="col-span-2">
                                                                <FormField
                                                                    control={form.control}
                                                                    name="comment"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormControl>
                                                                                <Textarea
                                                                                    placeholder="Commentaire sur l'offre destinée aux équipes chargés des entreprises"
                                                                                    onChange={e => {
                                                                                        field.onChange(e);
                                                                                    }}
                                                                                />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-4">
                                                        <h2 className="font-medium">Votre contact dans l'entreprise</h2>
                                                        <div className="grid gap-3 lg:grid-cols-2">
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="contact_first_name"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel required>Prénom</FormLabel>
                                                                            <FormControl>
                                                                                <Input placeholder="Prénom du contact" {...field} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="contact_last_name"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel required>Nom</FormLabel>
                                                                            <FormControl>
                                                                                <Input placeholder="Nom du contact" {...field} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="contact_email"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel>Adresse email</FormLabel>
                                                                            <FormControl>
                                                                                <Input placeholder="Email du contact" {...field} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                            <div>
                                                                <FormField
                                                                    control={form.control}
                                                                    name="contact_phone"
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel>
                                                                                Téléphone <u>personnel</u>
                                                                            </FormLabel>
                                                                            <FormControl>
                                                                                <Input placeholder="N° de téléphone du contact" {...field} />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                            <CardFooter className="flex justify-end gap-3">
                                                <Button type="submit" variant="default" disabled={form.formState.isLoading || fetcher.state !== "idle"}>
                                                    <NavigationStatus fetcher={fetcher}>Enregistrer l'offre</NavigationStatus>
                                                </Button>
                                            </CardFooter>
                                        </>
                                    )}
                                </Card>
                            </form>
                        </Form>
                    </div>
                </div>
            </div>
        </>
    );
}
